import { UserRound, BriefcaseBusiness, Laptop, MapPinned, Store, FilePenLine, ShieldCheck, MessagesSquare, Compass, Handshake, MailOpen, Scale, LifeBuoy, FileCheck, FileX, Clock, Receipt, CircleDollarSign, Landmark, TrendingDown, CalendarClock, FileText, FolderOpen, UserCheck, RefreshCcw, SearchCheck, CreditCard, Calculator, FileSpreadsheet, Lock } from "lucide-react";

export const CONTACT = {
    phoneDisplay: "",
    phoneHref: "",
    email: "support@freedomtaxfiler.com",
    emailHref: "mailto:support@freedomtaxfiler.com",
    address: "360 W Erie St, Chicago, IL 60654, USA",
    mapsHref: "https://www.google.com/maps/search/?api=1&query=360+W+Erie+St%2C+Chicago%2C+IL+60654%2C+USA",
};

export const NAV_LINKS = [
    { label: "Home", path: "/", slug: "home" },
    { label: "About Us", path: "/about", slug: "about" },
    { label: "Tax Services", path: "/services", slug: "services" },
    { label: "Audit Defense", path: "/audit-defense", slug: "audit-defense" },
    { label: "FAQ", path: "/faq", slug: "faq" },
    { label: "Contact Us", path: "/contact", slug: "contact" },
];

export const RESOURCES_NAV = {
    label: "Resources",
    children: [
        { label: "IRS News", path: "/irs-news", slug: "irs-news" },
        { label: "Taxpayer Tools", path: "/resources", slug: "resources" },
    ],
};

export const AUDIENCES = [
    "W-2 Employees",
    "Working Professionals",
    "H-1B & Visa Professionals",
    "Gig Workers & Freelancers",
    "Multi-State Filers",
    "Small-Business Owners",
];

export const SERVICES = [
    {
        slug: "individual",
        icon: UserRound,
        title: "Individual Tax Return Preparation",
        tagline: "Federal and state returns, prepared person by person.",
        description:
            "Your personal tax situation is never one-size-fits-all. We prepare federal and applicable state returns for individuals, reviewing income, deductions, and credits carefully so your return reflects your real life — accurately and on time.",
        bullets: [
            "Federal (1040) and applicable state returns",
            "Income, deduction, and credit review line by line",
            "E-file submission with confirmation follow-up",
        ],
    },
    {
        slug: "w2",
        icon: BriefcaseBusiness,
        title: "W-2 Tax Filing",
        tagline: "Straightforward W-2 employment filing, handled right.",
        description:
            "One W-2 or several, we bring your employment income together in a single, carefully prepared return. We reconcile your withholdings, check the deductions and credits that apply to you, and file with confidence.",
        bullets: [
            "One or multiple W-2s consolidated in a single return",
            "Withholding and pre-payment reconciliation",
            "Guidance on common deductions and credits",
        ],
    },
    {
        slug: "gig",
        icon: Laptop,
        title: "Gig Worker & Freelancer Taxes",
        tagline: "1099 income and self-employment, minus the guesswork.",
        description:
            "Driving, delivering, designing, consulting — if you earn 1099 income, your taxes work differently. We organize your income, sort deductible expenses, and help you plan ahead with quarterly estimated tax guidance.",
        bullets: [
            "1099-NEC / 1099-K income organization",
            "Business expense and mileage categorization",
            "Quarterly estimated tax guidance",
        ],
    },
    {
        slug: "multistate",
        icon: MapPinned,
        title: "Multi-State Tax Returns",
        tagline: "Lived or worked in more than one state? We sort it.",
        description:
            "Moved mid-year, worked across state lines, or remote from a different state than your employer? We prepare the federal return together with the applicable part-year and non-resident state returns, and review credits where states overlap.",
        bullets: [
            "Part-year and non-resident state returns",
            "State-by-state allocation of income",
            "Credit review where states overlap",
        ],
    },
    {
        slug: "smallbusiness",
        icon: Store,
        title: "Small-Business Tax Services",
        tagline: "For owners, side businesses, and growing teams.",
        description:
            "From a side hustle to an established operation, we prepare business returns and Schedule C filings with an owner's eye — reviewing expenses, assets, and deductions so your business pays only what it should.",
        bullets: [
            "Schedule C and applicable entity returns",
            "Expense, asset, and deduction review",
            "Filing calendar and deadline reminders",
        ],
    },
    {
        slug: "amended",
        icon: FilePenLine,
        title: "Amended Tax Returns",
        tagline: "Corrections to past filings, done cleanly.",
        description:
            "Found a forgotten form, a life change, or an error on a previously filed return? We review the original filing, prepare an accurate amended return, and explain clearly what changed and why it matters.",
        bullets: [
            "Careful review of the original return",
            "Preparation of amended (1040-X) returns",
            "Clear explanation of what changed and why",
        ],
    },
];

export const PROCESS = [
    {
        num: "01",
        title: "Reach Out",
        text: "Send an inquiry through our form or call us. Tell us a little about your situation — no documents needed at this stage.",
    },
    {
        num: "02",
        title: "Consultation",
        text: "We talk through your needs, confirm what applies to you, and tell you exactly what information to have ready. You will never be asked to send sensitive tax documents through our website form.",
    },
    {
        num: "03",
        title: "Preparation",
        text: "We prepare your return carefully, review it line by line, and flag anything that needs your attention along the way.",
    },
    {
        num: "04",
        title: "Review & Filing",
        text: "You review and approve. We file, confirm your submission, and walk you through what to expect next — including payment or refund status options.",
    },
];

export const FAQS = [
    {
        q: "How do I get started with Freedom Tax Filer?",
        a: "It starts with a simple conversation. Send us a note through the contact form or call us, and tell us briefly about your situation. We will respond with next steps and let you know what information to have ready for your consultation.",
    },
    {
        q: "Can you help me file an amended return?",
        a: "Yes. Start with the free initial review on our Tax Amendment page — tell us about your previously filed return and what concerns you, with no obligation to proceed. If an amendment makes sense, we'll explain the process and any applicable fees before any work begins.",
    },
    {
        q: "I received a letter from the IRS. What should I do?",
        a: "Don't ignore it — IRS notices carry response deadlines. Visit our Audit Defense page to see how we help, then contact us with a copy of the notice. We'll review it against your return, explain what it means in plain language, and help you prepare a response where your agreement covers it.",
    },
    {
        q: "Who will handle my tax return?",
        a: "Your return is prepared carefully and reviewed line by line before filing, and we're happy to walk you through it in plain language — what was filed, why, and what to keep for your records.",
    },
    {
        q: "Is there any cost to ask a question first?",
        a: "For tax amendments, the initial review is free with no obligation to proceed. For other services, we'll always explain the service scope and applicable fees clearly before any filing work begins, so you can decide with confidence.",
    },
    {
        q: "What documents will I need for my tax return?",
        a: "It depends on your situation. W-2 employees generally need their wage statements; gig workers and freelancers add 1099 forms and expense records; business owners bring their books. During your consultation we will give you a clear, specific list. We will never ask you to send sensitive tax documents through our website form — we arrange secure delivery when you become a client.",
    },
    {
        q: "Do you work with gig workers and freelancers?",
        a: "Yes. We prepare returns for rideshare and delivery drivers, independent contractors, and freelancers of every kind — including organizing 1099 income, categorizing deductible expenses, and guiding quarterly estimated payments.",
    },
    {
        q: "Can you handle returns involving more than one state?",
        a: "Yes. If you moved during the year, worked across state lines, or live in a different state than your employer, we prepare the applicable part-year and non-resident state returns alongside your federal return.",
    },
    {
        q: "What if I need to correct a return I already filed?",
        a: "We review the original return, determine whether an amendment is appropriate, and prepare an amended (1040-X) return with a clear explanation of what changed and why.",
    },
    {
        q: "Do you work with small businesses?",
        a: "Yes. We prepare Schedule C filings for sole proprietors as well as applicable returns for other entity types, and we review expenses, assets, and deductions with an owner's eye.",
    },
    {
        q: "Is my information safe?",
        a: "We treat your information with care. Our website form is for general inquiries only — it is not for tax documents or sensitive identifiers like Social Security numbers. When you become a client, we arrange secure channels for anything sensitive.",
    },
    {
        q: "Can I book a call instead of using the form?",
        a: "Absolutely. Send us a note through the contact form or the live chat, and we will talk through your situation. If we miss you, leave a message or send an email and we will get back to you promptly.",
    },
    {
        q: "What does tax preparation cost?",
        a: "It depends on the complexity of your return — a simple W-2 filing and a multi-state business return are different amounts of work. We scope your situation during the consultation, before any preparation begins, so you know where things stand.",
    },
];

export const VALUES = [
    {
        icon: ShieldCheck,
        title: "Accuracy first",
        text: "Every return is reviewed line by line before you ever see it. Details are the job.",
    },
    {
        icon: MessagesSquare,
        title: "Plain language",
        text: "We explain your taxes without jargon, so you always know where you stand.",
    },
    {
        icon: Compass,
        title: "Right-sized service",
        text: "From a single W-2 to a multi-state 1099 season, we scope the work your situation actually needs.",
    },
    {
        icon: Handshake,
        title: "Personal attention",
        text: "You work with a person who knows your situation — not a portal, not a queue.",
    },
];

export const MARQUEE_ITEMS = [
    "Individual Returns",
    "W-2 Filing",
    "Gig & Freelancer",
    "Multi-State",
    "Small Business",
    "Amended Returns",
];

export const IMAGES = {
    hero: "https://images.unsplash.com/photo-1761437855740-c894da924d79?fm=webp&q=75&w=2000",
    heroConsultant: "https://images.unsplash.com/photo-1758520145140-c2dd8e78fc02?fm=webp&q=70&w=2000",
    servicesHero: "https://images.unsplash.com/photo-1565164370954-8eac883fb7c8?fm=webp&q=75&w=2000",
    about: "https://images.unsplash.com/photo-1758518729685-f88df7890776?fm=webp&q=75&w=1400",
    preparer: "https://images.pexels.com/photos/6927546/pexels-photo-6927546.jpeg?auto=compress&cs=tinysrgb&fm=webp&w=1200",
    individual: "https://images.unsplash.com/photo-1772588627342-5ec373e236d8?fm=webp&q=75&w=1400",
    w2: "https://images.unsplash.com/photo-1713947503550-7c1bf16b8edf?fm=webp&q=75&w=1400",
    gig: "https://images.pexels.com/photos/4391221/pexels-photo-4391221.jpeg?auto=compress&cs=tinysrgb&fm=webp&w=1200",
    multistate: "https://images.pexels.com/photos/6960385/pexels-photo-6960385.jpeg?auto=compress&cs=tinysrgb&fm=webp&w=1200",
    smallBiz: "https://images.pexels.com/photos/8296970/pexels-photo-8296970.jpeg?auto=compress&cs=tinysrgb&fm=webp&w=1200",
    amended: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?fm=webp&q=75&w=1400",
    auditNotice: "https://images.unsplash.com/photo-1627618998627-70a92a874cc2?fm=webp&q=75&w=1600",
    amendment: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?fm=webp&q=80&w=2400",
    servicesWorkspace: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?fm=webp&q=75&w=1600",
};

export const AMENDMENT = {
    eyebrow: "Tax Amendment Services",
    support: "Think something was missed on your tax return? Tell us about your situation, and we'll help you understand your options. Start with a free initial review — no obligation to proceed.",
    cta: "Review My Previous Tax Return",
    freeLabel: "FREE TAX AMENDMENT REVIEW",
    homeParagraph: "Think you missed a tax deduction, overlooked an eligible credit, or made a mistake on a previously filed tax return? Don't guess—get it reviewed. Freedom Tax Filer offers a free initial review of your tax situation to help identify potential issues and determine whether an amended return may be worth considering. Find out what options may be available to you before deciding on your next step.",
    freeCta: "Get My FREE Tax Review",
    reassurance: "Free initial review. No obligation to proceed.",
};

export const EXPERIENCE = {
    established: 2017,
    yearsOfService: new Date().getFullYear() - 2017,
    paragraph: "Established in 2017, Freedom Tax Filer is committed to helping individuals, working professionals, gig workers, and small-business owners navigate their tax situations with clarity and confidence. We focus on identifying eligible deductions and credits, preparing accurate returns, and providing guidance tailored to each client's circumstances.",
    aboutParagraph: "Established in 2017, Freedom Tax Filer has grown around a simple promise: treat every return as if it were our own. Our commitment has stayed the same from the beginning — understand your situation before touching a form, apply every deduction and credit the law allows, and explain the finished return in plain language. That approach, paired with honest answers and guidance tailored to your circumstances, is how we serve individuals, working professionals, gig workers, and small-business owners with confidence, year after year.",
};

export const WHATSAPP = {
    href: "https://wa.me/message/FB4ENKBWDXCFB1",
    label: "Chat With Us on WhatsApp",
};

export const SERVICES_FEATURES = [
    {
        icon: UserRound,
        name: "Individual Tax Returns",
        title: "Personal Tax Filing",
        text: "We help eligible individuals prepare federal and applicable state income tax returns based on their circumstances.",
        linkLabel: "About individual returns",
        href: "/services",
    },
    {
        icon: BriefcaseBusiness,
        name: "W-2 Employee Returns",
        title: "W-2 Tax Filing",
        text: "We support employees reporting wage income, withholding, and other applicable income and deductions.",
        linkLabel: "About W-2 filing",
        href: "/services",
    },
    {
        icon: Laptop,
        name: "Self-Employed & 1099 Income",
        title: "Self-Employment Tax Support",
        text: "We help eligible freelancers, independent contractors, and gig workers report qualifying business income and expenses accurately.",
        linkLabel: "About self-employment filing",
        href: "/services",
    },
    {
        icon: FilePenLine,
        name: "Tax Amendments",
        title: "Review a Previously Filed Return",
        text: "We help customers determine whether a previously filed return may need correction — and explain the amendment process and applicable fees before work begins.",
        linkLabel: "About tax amendments",
        href: "/tax-amendment",
    },
    {
        icon: MapPinned,
        name: "Multi-State Tax Filing",
        title: "Multi-State Tax Support",
        text: "We assist eligible customers who need returns for multiple states, including those whose work or residence creates filing obligations in more than one state.",
        linkLabel: "About multi-state filing",
        href: "/services",
    },
    {
        icon: ShieldCheck,
        name: "Audit Defense Support",
        title: "Support Beyond Filing Day",
        text: "We explain the audit-defense assistance available to eligible customers under the applicable written agreement.",
        linkLabel: "About audit defense",
        href: "/audit-defense",
    },
];

export const TAX_FORMS = {
    title: "Tax Forms We Help You Navigate",
    intro: "A plain-English guide to the documents behind most filings. The forms you need depend on your situation — we'll tell you which ones apply to yours.",
    forms: [
        { icon: FileText, name: "Form 1040", text: "U.S. individual income tax return." },
        { icon: FileSpreadsheet, name: "Form W-2", text: "Wage and tax statement provided by an employer." },
        { icon: Receipt, name: "Form 1099-NEC", text: "Reports qualifying nonemployee compensation." },
        { icon: CreditCard, name: "Form 1099-K", text: "Reports certain payment transactions." },
        { icon: Store, name: "Schedule C", text: "Reports income and expenses from a qualifying sole proprietorship or business activity." },
        { icon: Calculator, name: "Schedule SE", text: "Calculates self-employment tax when applicable." },
        { icon: FilePenLine, name: "Form 1040-X", text: "Used to amend a previously filed individual income tax return." },
        { icon: MapPinned, name: "State Income Tax Returns", text: "Applicable state forms based on the customer's filing requirements." },
    ],
};

export const SERVICE_BENEFITS = [
    {
        icon: UserCheck,
        title: "Personalized Service",
        text: "Support tailored to each customer's tax situation.",
    },
    {
        icon: Receipt,
        title: "Transparent Pricing",
        text: "Clear fees and service scope before filing work begins.",
    },
    {
        icon: Lock,
        title: "Secure Document Handling",
        text: "A privacy-conscious process for handling sensitive tax documents.",
    },
    {
        icon: LifeBuoy,
        title: "Support After Filing",
        text: "A clear process for customers who have questions or receive a covered tax notice.",
    },
];

export const SERVICES_CTA = {
    headline: "Let's Make Tax Season Simpler.",
    text: "Tell us about your tax situation, and we'll help you understand the appropriate next steps.",
    cta: "Start Your Tax Filing",
};

export const PROTECTION = {
    eyebrow: "Our Taxpayer Protection Promise",
    supportHeadline: "Your taxes. Our responsibility. Our commitment.",
    intro: [
        "At Freedom Tax Filer, our commitment doesn't end when your tax return is filed. We stand behind our preparation work and provide eligible customers with defined support if a covered return requires correction or receives an IRS notice.",
        "Our goal is to give you clarity, professional assistance, and confidence—not leave you wondering what to do next.",
    ],
    audit: {
        icon: ShieldCheck,
        headline: "IRS Questions Your Return? We're Here to Help.",
        description: "Receiving an IRS notice can be stressful. Our Audit Defense Commitment explains the support eligible customers receive when the IRS questions a covered tax return prepared through Freedom Tax Filer.",
        benefits: [
            { icon: FileText, text: "IRS notice assistance." },
            { icon: FolderOpen, text: "Guidance on supporting documentation." },
            { icon: FilePenLine, text: "Assistance preparing responses to covered examinations." },
            { icon: UserCheck, text: "Qualified professional assistance where included in the agreement." },
            { icon: MessagesSquare, text: "Clear communication about next steps." },
            { icon: CircleDollarSign, text: "No additional professional fees for the audit-defense services expressly covered by the customer's agreement." },
        ],
        explore: "Explore Audit Defense",
        exploreHref: "/audit-defense",
        pdfLabel: "View Audit Defense Commitment",
        pdfPath: "/docs/audit-defense-commitment.pdf",
    },
    guarantee: {
        icon: FileCheck,
        headline: "If We Make a Mistake, We Stand Behind Our Work.",
        description: "We take our tax preparation responsibilities seriously. If an error attributable to Freedom Tax Filer's preparation is identified, we will review the issue and take appropriate corrective action within the scope of our written commitment.",
        benefits: [
            { icon: SearchCheck, text: "Review of errors attributable to our preparation." },
            { icon: RefreshCcw, text: "Appropriate corrective action for covered preparation errors." },
            { icon: FileX, text: "Assistance addressing omitted eligible deductions or incorrect deductions resulting from our preparation." },
            { icon: ShieldCheck, text: "Audit-defense support under the applicable written agreement." },
            { icon: CircleDollarSign, text: "No additional professional fees for covered corrective work." },
            { icon: MessagesSquare, text: "Clear communication throughout the process." },
        ],
        explore: "Explore Our Responsibility Guarantee",
        pdfLabel: "View Responsibility Guarantee",
        pdfPath: "/docs/tax-filing-responsibility-guarantee.pdf",
    },
    banner: {
        headline: "Our Commitment Doesn't End on Filing Day.",
        text: "Your peace of mind matters. If a covered issue arises, we'll help you understand the situation, explain your options, and provide the assistance described in your written agreement.",
        cta: "Get Started With Freedom Tax Filer",
        ctaHref: "/contact",
    },
    guaranteeModal: {
        title: "Tax Filing Responsibility Guarantee",
        subtitle: "How the guarantee works",
        steps: [
            { title: "Contact us", text: "Reach out by phone, email, or WhatsApp with the details of your concern." },
            { title: "We review", text: "We review the return in question and the issue you've raised, together with any documentation you provide." },
            { title: "Corrective action", text: "If an error is attributable to our preparation, we take appropriate corrective action within the scope of the written commitment." },
            { title: "Clear communication", text: "We explain what we found, what we recommend, and the next steps — throughout the process." },
        ],
        provideTitle: "What customers provide",
        provide: "A copy of the return in question, any IRS or state notices received, and documentation relevant to the issue.",
        coveredTitle: "What is covered",
        covered: "Errors attributable to Freedom Tax Filer's preparation, and the corrective work described in your written commitment.",
        exclusionsTitle: "Eligibility and limitations",
        exclusions: [
            "The guarantee applies to returns prepared by Freedom Tax Filer under a signed agreement.",
            "The exact coverage, coverage period, and any exclusions are stated in your written commitment.",
            "Issues arising from information provided by the client, or from changes in law after filing, are not preparation errors.",
            "This guarantee does not mean Freedom Tax Filer pays taxes, interest, or penalties legally owed.",
            "Audit-defense support follows the applicable written agreement.",
        ],
        note: "This summary is provided for convenience. Your written commitment governs in the event of any difference.",
    },
    faqTitle: "Protection Questions, Answered.",
    faqs: [
        {
            q: "What happens if the IRS audits my tax return?",
            a: "If the IRS examines an eligible return covered by our Audit Defense Commitment, contact Freedom Tax Filer promptly and provide a copy of the notice. We'll explain the next steps and provide the assistance specified in your written agreement.",
        },
        {
            q: "Will I pay additional fees if the IRS questions my return?",
            a: "No additional professional fees will be charged for the audit-defense services expressly included in your agreement. Services outside the stated coverage may involve additional fees, which must be disclosed in accordance with the agreement.",
        },
        {
            q: "What happens if Freedom Tax Filer makes a mistake?",
            a: "We'll review any identified error attributable to our preparation and take appropriate corrective action within the scope of our Tax Filing Responsibility Guarantee.",
        },
        {
            q: "Does audit defense guarantee that I won't owe additional taxes?",
            a: "No. Audit defense provides defined assistance; it cannot guarantee an IRS decision or eliminate taxes, interest, or penalties legally owed.",
        },
        {
            q: "Does the protection cover every tax year and every state?",
            a: "Coverage depends on the tax year, jurisdiction, and services identified in the customer's written agreement. Customers should review their individual coverage before purchasing.",
        },
    ],
};

export const AUDIT_PROGRAM = {
    eyebrow: "Audit Defense & IRS Notice Assistance",
    intro: "Your relationship with Freedom Tax Filer doesn't have to end when your tax return is filed. We provide ongoing tax notice and audit assistance for eligible returns prepared by our firm, helping you understand IRS correspondence and determine the appropriate next steps.",
    promise: "Filed with Freedom Tax Filer? We're here to help you navigate eligible IRS notices and audit inquiries—even after filing.",
};

export const AUDIT_BENEFITS = [
    {
        icon: MailOpen,
        title: "IRS Notice Assistance",
        text: "Help understanding IRS letters, notices, and requests for additional information related to eligible returns.",
    },
    {
        icon: Scale,
        title: "Audit Support",
        text: "Guidance and assistance responding to qualifying IRS examination or audit inquiries.",
    },
    {
        icon: Compass,
        title: "Next-Step Guidance",
        text: "Clear explanations of what a notice means, what information may be needed, and what actions to consider.",
    },
    {
        icon: LifeBuoy,
        title: "Ongoing Support",
        text: "Access to our team after filing, subject to the terms of our service agreement.",
    },
    {
        icon: ShieldCheck,
        title: "Peace of Mind",
        text: "You don't have to figure out an IRS notice on your own.",
    },
];

export const AUDIT_TERMS = [
    {
        icon: FileCheck,
        title: "Who qualifies",
        text: "Assistance applies only to eligible returns prepared by Freedom Tax Filer. The exact scope of help is set out in the written service agreement you review before purchasing.",
    },
    {
        icon: FileX,
        title: "What's not covered",
        text: "Notices about returns we did not prepare, matters outside the agreement's scope, and services such as full audit representation, which are arranged separately. If something falls outside coverage, we will tell you plainly.",
    },
    {
        icon: Clock,
        title: "Response-time expectations",
        text: "Once we receive your notice and the information we need, we review it and outline your next steps — typically within a few business days. Complex matters can take longer, and we will keep you informed along the way.",
    },
    {
        icon: Receipt,
        title: "Fees, up front",
        text: "Your service agreement explains what is included at no extra charge. Anything that could involve an additional fee is explained and quoted before you commit — never after.",
    },
];

export const AUDIT_NOTICE_STEPS = [
    "Reach out and tell us a notice arrived. Please don't send documents through the website form — we'll arrange secure delivery.",
    "We review the notice against your return and explain what it means and what it asks for.",
    "We help you prepare a response and stay with you until the matter is resolved or escalated by agreement.",
];

export const AUDIT_NOTICE_INFO = {
    title: "Understanding Your IRS Notice",
    intro: "Receiving an IRS letter doesn't automatically mean an audit. Many notices simply report a change, request more information, or remind you of a deadline. What matters is acting promptly — every notice states what it's about and when a response is due.",
    examples: [
        { title: "Information-match notices", text: "The IRS compares the income and deductions reported on your return against what employers and banks reported to them. A notice may propose changes if the numbers don't line up." },
        { title: "Adjustment notices", text: "Some notices state that the IRS corrected or changed something on your return, such as a calculation, a credit, or a payment application." },
        { title: "Balance-due and reminder letters", text: "Others relate to tax, interest, or penalties the IRS believes is owed, or simply remind you about a return or payment that appears to be missing." },
    ],
    note: "Whichever letter lands in your mailbox, don't respond to the IRS on your own before talking to us — and never ignore the deadline printed on the notice.",
};

export const AUDIT_FAQ = [
    {
        q: "Can you guarantee the outcome of an IRS examination?",
        a: "No. Audit defense provides defined assistance under your written agreement — it cannot guarantee an IRS decision or eliminate taxes, interest, or penalties legally owed.",
    },
    {
        q: "What if I disagree with the IRS's proposed changes?",
        a: "Many notices include a response window and options for replying or requesting further review. Bring us the notice and we'll explain what it asks for, what your response options generally are, and how we can help under your agreement.",
    },
    {
        q: "How quickly should I act after receiving a notice?",
        a: "Promptly. Notices carry deadlines, and acting early gives us time to review the notice against your return, gather any documentation, and prepare a clear response without last-minute pressure.",
    },
];

export const AMENDMENT_FAQ = [
    {
        q: "Is the initial review really free?",
        a: "Yes. The initial review of your previously filed return is free and comes with no obligation to proceed. If an amendment makes sense, we'll explain the process and any applicable fees before any work begins.",
    },
    {
        q: "How do I know if I need an amended return?",
        a: "Common reasons include a missed deduction or credit, income that wasn't reported, a filing status that should have been different, or an IRS notice about a filed return. The free review helps determine whether amending is appropriate for your situation — sometimes the original return is fine as filed.",
    },
    {
        q: "What information should I have ready?",
        a: "A copy of the return in question, any IRS or state notices you received, and documents related to your concern — for example, an income statement or a deduction record. Tell us the tax year or years you'd like reviewed.",
    },
];

export const VALUE_PROPS = [
    {
        icon: CircleDollarSign,
        title: "Maximize Your Eligible Refund",
        text: "We carefully review your tax information to identify applicable deductions, credits, and withholding that may increase your refund or reduce the amount you owe.",
    },
    {
        icon: Landmark,
        title: "Don't Pay More Tax Than You Legally Owe",
        text: "Our tax preparation process focuses on accuracy, eligible tax benefits, and understanding your individual financial situation.",
    },
    {
        icon: TrendingDown,
        title: "Help Reduce Your Tax Bill",
        text: "We identify legitimate tax-saving opportunities based on your income, expenses, filing status, and applicable tax rules.",
    },
    {
        icon: LifeBuoy,
        title: "Support Beyond Filing",
        text: "We help eligible clients understand IRS notices and access the post-filing assistance included in their service agreement.",
    },
    {
        icon: UserRound,
        title: "Tax Preparation Built Around You",
        text: "Whether you're a W-2 employee, an H-1B professional, a gig worker, a freelancer, or a small-business owner, we tailor our preparation process to your tax situation.",
    },
];

export const CLOSING_MESSAGE =
    "Your hard-earned money matters. Let's make sure your taxes are prepared carefully and every eligible tax benefit is considered.";

export const TAX_BENEFIT_DISCLAIMER =
    "All tax benefits depend on your individual circumstances and applicable tax law. We don't guarantee a maximum refund, a specific refund amount, a reduction in tax liability, or eligibility for any deduction or credit.";

export const IRS_NEWS = {
    eyebrow: "From the Official IRS Newsroom",
    heroText:
        "The latest news, reminders, and announcements published by the Internal Revenue Service — synced automatically from the official IRS.gov newsroom, so what you're reading is always current.",
    liveBadge: "Synced automatically from IRS.gov",
    homeEyebrow: "Straight From the IRS Newsroom",
    homeText:
        "Fresh from the official IRS newsroom — filing deadlines, tax tips, refund updates, and scam alerts, pulled in automatically from IRS.gov.",
    viewAll: "View All IRS Updates",
    readMore: "Read Official IRS Update",
    sourceBadge: "Official IRS Release",
    attribution: "Source: IRS.gov",
    syncedNote: "Automatically synced from the official IRS newsroom. Last check:",
    emptyText:
        "No IRS updates are available right now. New items appear here automatically as the IRS publishes them — please check back soon.",
    disabledText: "The IRS news section is temporarily turned off. Please check back soon.",
    errorText:
        "We're having a brief technical issue loading IRS updates. Articles already synced remain saved and will reappear automatically — please check back shortly.",
    disclaimer:
        "The Internal Revenue Service (IRS) is a U.S. government agency. Freedom Tax Filer is an independent tax-preparation business and is not affiliated with, endorsed by, sponsored by, or acting on behalf of the IRS or any government agency. Headlines, publication dates, and short summaries on this page come from official IRS newsroom releases and link to the original articles on IRS.gov, which remain the property of their publisher. This information is provided for general awareness only and is not tax, legal, or financial advice.",
    ctaTitle: "Questions About an IRS Announcement?",
    ctaText: "If an IRS update affects your tax situation, our team can help you understand what it means for you and your return.",
    ctaButton: "Discuss My Tax Situation",
};

export const TAXPAYER_TOOLS = {
    subtitle: "Access official IRS tools to check your refund, track an amended return, obtain tax records, and make federal tax payments.",
    badge: "Official tools hosted on IRS.gov",
    ctaText: "These official IRS tools show where your refund or amended return stands and help with tax records and payments. For preparation, amendments, and notice assistance, our team is one message away.",
    ctaButton: "Discuss My Tax Situation",
    disclaimer: "Freedom Tax Filer is an independent tax-preparation service and is not affiliated with or endorsed by the Internal Revenue Service. These links direct you to official IRS resources. Refund processing times and return status updates are determined by the IRS.",
    cards: [
        {
            icon: CircleDollarSign,
            title: "Where's My Refund?",
            description: "Check the status of your federal income tax refund using the official IRS refund tracking tool.",
            button: "Check My Refund",
            url: "https://www.irs.gov/wheres-my-refund",
        },
        {
            icon: FilePenLine,
            title: "Amended Return Status",
            description: "Check the status of an amended federal income tax return using the IRS amended return tracking tool.",
            button: "Track Amended Return",
            url: "https://www.irs.gov/filing/wheres-my-amended-return",
        },
        {
            icon: FolderOpen,
            title: "Get Your Tax Transcript",
            description: "Visit the IRS website to access available tax transcripts and other tax records.",
            button: "Get Tax Records",
            url: "https://www.irs.gov/individuals/get-transcript",
        },
        {
            icon: CreditCard,
            title: "Make an IRS Payment",
            description: "Visit the official IRS website to review available federal tax payment options.",
            button: "Make a Payment",
            url: "https://www.irs.gov/payments",
        },
    ],
};

export const REFUND_GUIDE = {
    eyebrow: "Refund Timing Guide",
    titleA: "How Refund Timing",
    titleB: "Usually Works.",
    intro: "A plain-English guide to refund timing, based on the IRS's published guidance. The IRS determines your refund status and processing time — the official tracker above is always the authoritative source.",
    timeline: [
        {
            icon: Clock,
            title: "E-filed returns",
            text: "Per the IRS, e-filed refunds typically arrive about 3 weeks from the date you e-file — and your refund status is usually available to check within 24 hours of e-filing a current-year return.",
        },
        {
            icon: CalendarClock,
            title: "Paper (mailed) returns",
            text: "Mailed paper returns take longer: status generally becomes available about 4 weeks after you mail your return, and the IRS says refunds take 6 or more weeks from the date it receives it.",
        },
        {
            icon: FilePenLine,
            title: "Amended returns",
            text: "Amended returns take longer because the IRS must compare and review both the original and the amended return. Track progress with the amended return tool above.",
        },
    ],
    delaysTitle: "Common reasons a refund can take longer",
    delays: [
        { icon: FileX, title: "Common mistakes", text: "An unsigned return or a math error can hold things up while the IRS corrects or reviews the return." },
        { icon: CalendarClock, title: "Earned Income Credit claims", text: "By law, refunds on returns claiming the Earned Income Credit are held until mid-February to prevent fraud." },
        { icon: SearchCheck, title: "Additional Child Tax Credit claims", text: "The IRS notes this credit can be complicated and requires more time to review." },
        { icon: FilePenLine, title: "Amended returns", text: "When a return is amended, the IRS must compare and review both returns, which adds processing time." },
        { icon: UserCheck, title: "Injured spouse relief requests", text: "These requests are processed manually by the IRS, so they take longer than standard refunds." },
        { icon: TrendingDown, title: "Offset toward certain debts", text: "A refund may be reduced to pay certain past-due taxes or debts, such as child support or state income tax. The Bureau of the Fiscal Service mails a notice when this happens." },
    ],
    note: "Freedom Tax Filer can't look up, expedite, or guarantee your refund — timing is determined by the IRS. If the tracker shows a delay or a notice arrives, we're happy to help you understand what it means and what to do next.",
    officialLabel: "Official IRS refund help",
    officialUrl: "https://www.irs.gov/refunds",
};
